import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import { useDispatch } from "react-redux";
import MainLayout from "../../../components/layout/MainLayout";
import Breadcrumbs from "../../../components/common/Breadcrumbs";
import QOLForm from "./QOLForm";
import { createQolAssessment } from "../../../store/slices/qolAssessmentsSlice";
import { fetchPatient } from "../../../store/slices/patientsSlice";


const AddQol = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { state } = useLocation();
    const { caseId, patientId } = state || {};

    const [formData, setFormData] = useState({
        caseId,
        patientId,
        clientName: "",
        diagnosis: "",
        assessedBy: "",
        q1: "", q2: "", q3: "", q4: "", q5: "", q6: "", q7: "", q8: "",
        q9: "", q10: "", q11: "", q12: "", q13: "", q14: "", q15: "", q16: ""
    });

    useEffect(() => {
        if (patientId) {
            dispatch(fetchPatient(patientId))
                .unwrap()
                .then((patient) => {
                    if (patient) {
                        setFormData((prev) => ({
                            ...prev,
                            clientName: `${patient.first_name || ""} ${patient.last_name || ""}`.trim(),
                        }));
                    }
                })
                .catch((err) => console.error("Failed to prefill patient name for QOLS form:", err));
        }
    }, [dispatch, patientId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const calculateTotal = () => {
        const fields = Object.keys(formData).filter(k => k.startsWith("q"));
        return fields.reduce((sum, field) => sum + (parseInt(formData[field] || 0, 10)), 0);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const totalScore = calculateTotal();

        if (totalScore < 16) {
            Swal.fire("Incomplete", "Please complete all 16 items.", "warning");
            return;
        }

        // Map each question to its score and value
        const questionMap = [
            { scoreKey: "q1_material_score", valueKey: "q1_material_value", formKey: "q1" },
            { scoreKey: "q2_health_score", valueKey: "q2_health_value", formKey: "q2" },
            { scoreKey: "q3_relationship_score", valueKey: "q3_relationship_value", formKey: "q3" },
            { scoreKey: "q4_having_score", valueKey: "q4_having_value", formKey: "q4" },
            { scoreKey: "q5_spouse_score", valueKey: "q5_spouse_value", formKey: "q5" },
            { scoreKey: "q6_friends_score", valueKey: "q6_friends_value", formKey: "q6" },
            { scoreKey: "q7_helping_score", valueKey: "q7_helping_value", formKey: "q7" },
            { scoreKey: "q8_participation_score", valueKey: "q8_participation_value", formKey: "q8" },
            { scoreKey: "q9_learning_score", valueKey: "q9_learning_value", formKey: "q9" },
            { scoreKey: "q10_selfunderstanding_score", valueKey: "q10_selfunderstanding_value", formKey: "q10" },
            { scoreKey: "q11_work_score", valueKey: "q11_work_value", formKey: "q11" },
            { scoreKey: "q12_creativity_score", valueKey: "q12_creativity_value", formKey: "q12" },
            { scoreKey: "q13_socializing_score", valueKey: "q13_socializing_value", formKey: "q13" },
            { scoreKey: "q14_entertainment_score", valueKey: "q14_entertainment_value", formKey: "q14" },
            { scoreKey: "q15_recreation_score", valueKey: "q15_recreation_value", formKey: "q15" },
            { scoreKey: "q16_independence_score", valueKey: "q16_independence_value", formKey: "q16" },
        ];

        // Options mapping
        const options = [
            { value: "7", label: "Delighted" },
            { value: "6", label: "Pleased" },
            { value: "5", label: "Mostly Satisfied" },
            { value: "4", label: "Mixed" },
            { value: "3", label: "Mostly Dissatisfied" },
            { value: "2", label: "Unhappy" },
            { value: "1", label: "Terrible" }
        ];

        // Build payload dynamically
        const payload = {
            patient_id: formData.patientId,
            case_id: formData.caseId,
            client_name: formData.clientName,
            diagnosis: formData.diagnosis,
            assessed_by: formData.assessedBy,
        };

        questionMap.forEach(q => {
            const score = formData[q.formKey];
            const value = options.find(opt => opt.value === score)?.label || "";
            payload[q.scoreKey] = score;
            payload[q.valueKey] = value;
        });

        try {
            const res = await dispatch(createQolAssessment(payload)).unwrap();

            Swal.fire({
                title: "Saved!",
                text: `QOLS Assessment completed. Total Score: ${totalScore}`,
                icon: "success",
                timer: 2000,
                showConfirmButton: false
            });

            navigate(`/phase04/qol/edit/${res.id || res.data?.id || (res && res[0]?.id)}`, { state: { patientId: patientId || (res && res.patient_id), caseId: caseId || (res && res.case_id) } });
        } catch (err) {
            Swal.fire("Error", "Failed to save QOLS assessment", "error");
        }
    };

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <Breadcrumbs />
                <div className="card">
                    <div className="card-body">
                        <form onSubmit={handleSubmit}>
                            <QOLForm formData={formData} handleChange={handleChange} />
                            <div className="col-12 d-flex justify-content-end mt-3">
                                <div className="col-md-2 d-flex justify-content-end">
                                    <button
                                        type="submit"
                                        className="btn btn-primary px-4"
                                    >
                                        <i className="ri-save-2-line me-1"></i>
                                        Save
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default AddQol;
